import * as React from 'react';
import Chart from 'chart.js';
import { themeDark } from 'styles/theme';
import { BarChartStrikeProps, ValueType } from './props';
import {
  BASE_CHART_OPTIONS,
  BASE_X_TICK_STYLE,
  buildYAxisConfig,
  buildNetDataset,
  buildTotalDatasets,
  getShortLabel,
  getChartFromTooltipContext,
} from './chartConfig';
import {
  getOrCreateTooltipEl,
  removeTooltipEl,
  positionTooltip,
  tooltipRow,
  wrapTooltipTable,
} from './tooltipHelpers';

let nextStrikeInstanceId = 0;

interface ProcessedStrikeData {
  strike: number;
  unusualCalls: number;
  normalCalls: number;
  totalCalls: number;
  unusualPuts: number;
  normalPuts: number;
  totalPuts: number;
  bullishValue: number;
  bearishValue: number;
  netValue: number;
  neutralValue: number;
}

const MAX_STRIKES = 15;

const BarChartStrikeTrends: React.FC<React.PropsWithChildren<BarChartStrikeProps>> = ({
  tradesByStrike: originalTradesByStrike,
  label,
  prefix,
  formatter,
  dataType,
  valueType = ValueType.Net,
}) => {
  const chartWrapperRef = React.useRef<HTMLCanvasElement>(null);
  const chartRef = React.useRef<Chart | undefined>(undefined);
  const instanceSuffix = React.useRef(++nextStrikeInstanceId).current;
  const TOOLTIP_ID = `chartjs-tooltip-strike-trends-${instanceSuffix}`;
  const CARET_ID = `tooltip-caret-strike-${instanceSuffix}`;

  const tradesByStrike = React.useMemo(() => {
    const isVolume = dataType === 'volume';

    const withTotal = originalTradesByStrike.map((t) => ({
      ...t,
      totalActivity: isVolume ? t.callVolume + t.putVolume : t.callPremium + t.putPremium,
    }));

    return withTotal
      .sort((a, b) => b.totalActivity - a.totalActivity)
      .slice(0, MAX_STRIKES)
      .sort((a, b) => a.strike - b.strike);
  }, [originalTradesByStrike, dataType]);

  const processedData = React.useMemo((): ProcessedStrikeData[] => {
    return tradesByStrike.map((t) => {
      const isVolume = dataType === 'volume';

      const unusualCalls = isVolume ? t.unusualCallVolume : t.unusualCallPremium;
      const unusualPuts = isVolume ? t.unusualPutVolume : t.unusualPutPremium;
      const totalCalls = isVolume ? t.callVolume : t.callPremium;
      const totalPuts = isVolume ? t.putVolume : t.putPremium;
      const totalBullish = isVolume
        ? (t.bullishVolume ?? totalCalls)
        : (t.bullishPremium ?? totalCalls);
      const totalBearish = isVolume
        ? (t.bearishVolume ?? totalPuts)
        : (t.bearishPremium ?? totalPuts);
      const neutralValue = isVolume ? t.neutralVolume || 0 : t.neutralPremium || 0;
      const netValue = totalBullish - totalBearish;

      return {
        strike: t.strike,
        unusualCalls,
        normalCalls: Math.max(totalCalls - unusualCalls, 0),
        totalCalls,
        unusualPuts,
        normalPuts: Math.max(totalPuts - unusualPuts, 0),
        totalPuts,
        bullishValue: totalBullish,
        bearishValue: totalBearish,
        netValue,
        neutralValue,
      };
    });
  }, [tradesByStrike, dataType]);

  const labels = React.useMemo(() => {
    return tradesByStrike.map((t) => `$${t.strike}`);
  }, [tradesByStrike]);

  // Cleanup tooltip DOM element on unmount
  React.useEffect(() => {
    return () => {
      removeTooltipEl(TOOLTIP_ID);
    };
  }, []);

  React.useEffect(() => {
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = undefined;
    }

    const ctx = chartWrapperRef.current?.getContext('2d');
    if (!ctx || processedData.length === 0) return;

    const currentProcessedData = processedData;
    const isNetMode = valueType === ValueType.Net;
    const yAxisLabel = label === 'Volume' ? 'Contracts' : 'Premium';

    const yAxisConfig = buildYAxisConfig({
      isNetMode,
      yAxisLabel,
      prefix,
      formatter,
      netValues: isNetMode ? currentProcessedData.map((t) => t.netValue) : undefined,
    });

    const shortLabel = getShortLabel(label);
    const datasets = isNetMode
      ? buildNetDataset(currentProcessedData, (t) => t.netValue, shortLabel)
      : buildTotalDatasets(currentProcessedData, {
          unusualCalls: (t) => (t.totalCalls === 0 ? null : t.unusualCalls),
          normalCalls: (t) => (t.totalCalls === 0 ? null : t.normalCalls),
          unusualPuts: (t) => (t.totalPuts === 0 ? null : t.unusualPuts),
          normalPuts: (t) => (t.totalPuts === 0 ? null : t.normalPuts),
        });

    chartRef.current = new Chart(ctx, {
      type: 'bar',
      data: {
        datasets,
        labels: labels,
      },
      options: {
        ...BASE_CHART_OPTIONS,
        tooltips: {
          enabled: false,
          custom: function (tooltipModel) {
            const tooltipEl = getOrCreateTooltipEl(TOOLTIP_ID);

            if (tooltipModel.opacity === 0) {
              tooltipEl.style.opacity = '0';
              return;
            }

            const dataPoint = tooltipModel.dataPoints?.[0];
            if (!dataPoint) return;

            const index = dataPoint.index as number;
            const datasetIndex = dataPoint.datasetIndex as number;
            const tradeData = currentProcessedData[index];
            const chart = getChartFromTooltipContext(this);
            const strikeLabel = chart.data.labels[index];

            if (!tradeData) return;

            const position = chart.canvas.getBoundingClientRect();
            const caretX = position.left + tooltipModel.caretX;
            const caretY = position.top + tooltipModel.caretY;

            let rows = '';

            if (isNetMode) {
              const netValue = tradeData.netValue;
              const totalBullish = tradeData.bullishValue || 0;
              const totalBearish = tradeData.bearishValue || 0;
              const neutralValue = tradeData.neutralValue || 0;
              const total = totalBullish + totalBearish;
              const callPercent = total > 0 ? ((totalBullish / total) * 100).toFixed(0) : '0';
              const putPercent = total > 0 ? ((totalBearish / total) * 100).toFixed(0) : '0';
              const isPositive = netValue >= 0;
              const netSign = isPositive ? '+' : '-';

              rows =
                tooltipRow('Strike', strikeLabel) +
                tooltipRow(
                  `Total Bullish ${label}`,
                  `${prefix}${formatter(totalBullish)}`,
                  themeDark.color.graphCall,
                ) +
                tooltipRow(
                  `Total Bearish ${label}`,
                  `${prefix}${formatter(totalBearish)}`,
                  themeDark.color.graphPut,
                ) +
                tooltipRow(
                  `Net ${label}`,
                  `${netSign}${prefix}${formatter(Math.abs(netValue))}`,
                  isPositive ? themeDark.color.graphCall : themeDark.color.graphPut,
                ) +
                (neutralValue
                  ? tooltipRow(
                      'Neutral',
                      `${prefix}${formatter(neutralValue)}`,
                      themeDark.color.textLight,
                    )
                  : '') +
                tooltipRow('% Bullish', `${callPercent}%`, undefined, false) +
                tooltipRow('% Bearish', `${putPercent}%`, undefined, false);
            } else {
              const isCall = datasetIndex <= 1;
              const total = isCall ? tradeData.totalCalls : tradeData.totalPuts;
              const unusual = isCall ? tradeData.unusualCalls : tradeData.unusualPuts;
              const normal = isCall ? tradeData.normalCalls : tradeData.normalPuts;
              const unusualPercent = total > 0 ? ((unusual / total) * 100).toFixed(1) : '0.0';
              const side = isCall ? 'Call' : 'Put';
              const sideColor = isCall ? themeDark.color.graphCall : themeDark.color.graphPut;

              rows =
                tooltipRow('Strike', strikeLabel) +
                tooltipRow('Side', side, sideColor) +
                tooltipRow(`Total ${label}`, `${prefix}${formatter(total)}`) +
                tooltipRow(`Unusual ${label}`, `${prefix}${formatter(unusual)}`) +
                tooltipRow(`Normal ${label}`, `${prefix}${formatter(normal)}`) +
                tooltipRow('Unusual %', `${unusualPercent}%`, undefined, false);
            }

            tooltipEl.innerHTML = wrapTooltipTable(rows, CARET_ID);
            positionTooltip(tooltipEl, caretX, caretY, CARET_ID);
          },
        },
        scales: {
          yAxes: [yAxisConfig],
          xAxes: [
            {
              stacked: !isNetMode,
              scaleLabel: {
                display: true,
                labelString: 'Strike Price',
                fontColor: themeDark.color.textLight,
                fontSize: 11,
              },
              ticks: BASE_X_TICK_STYLE,
            },
          ],
        },
      },
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = undefined;
      }
    };
  }, [processedData, labels, label, prefix, formatter, valueType]);

  return (
    <div style={{ maxHeight: '100%', height: '100%', width: '100%' }}>
      <canvas ref={chartWrapperRef} style={{ height: '100%', width: '100%' }} />
    </div>
  );
};

export default BarChartStrikeTrends;
